import React, { useEffect, useState } from 'react';

/** ------------------------------------------------------------------------
 * Games JSON uploader (uses /api/upload/json-direct)
 * Accepts ONLY filenames like games_week_X.json; supports overwrite
 * ---------------------------------------------------------------------- */
function JsonUpload({ backendBase }) {
  const [file, setFile] = useState(null);
  const [force, setForce] = useState(false);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  async function handleUpload() {
    if (!file) {
      setMsg({ kind: 'error', text: 'Choose a file first.' });
      return;
    }
    const okName = /^games[-_ ]?week[-_ ]?\d+(?:\.json)?$/i.test(file.name.trim());
    if (!okName) {
      setMsg({ kind: 'error', text: 'Filename must be games_week_X.json' });
      return;
    }

    setBusy(true); setMsg(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('force', String(force));

      const res = await fetch(`${backendBase}/api/upload/json-direct`, { method: 'POST', body: fd });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        if (res.status === 409) {
          const overwrite = window.confirm((data?.message || 'File already exists.') + '\nOverwrite?');
          if (overwrite) {
            setForce(true);
            // re-run with force
            const fd2 = new FormData();
            fd2.append('file', file);
            fd2.append('force', 'true');
            const r2 = await fetch(`${backendBase}/api/upload/json-direct`, { method: 'POST', body: fd2 });
            const d2 = await r2.json().catch(() => ({}));
            if (!r2.ok) setMsg({ kind: 'error', text: d2?.error || 'Upload failed.' });
            else setMsg({ kind: 'ok', text: `Saved as ${d2.savedAs || 'games file'}` });
          }
        } else {
          setMsg({ kind: 'error', text: data?.error || 'Upload failed.' });
        }
      } else {
        setMsg({ kind: 'ok', text: `Saved as ${data.savedAs || 'games file'}` });
        setFile(null); setForce(false);
      }
    } catch (e) {
      setMsg({ kind: 'error', text: e.message || 'Network error.' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ border: '1px solid #ddd', padding: 12, borderRadius: 6 }}>
      <h3 style={{ marginTop: 0 }}>Import Games JSON</h3>
      <div style={{ marginBottom: 6, color: '#555' }}>
        Expected: <code>[{'{'}date, team1, spread1, team2, spread2{'}'}]</code>
      </div>
      <div style={{ marginBottom: 8 }}>
        <input type="file" accept=".json" onChange={(e) => setFile(e.target.files?.[0] || null)} />
      </div>
      <label style={{ display: 'inline-flex', alignItems: 'center', marginRight: 12 }}>
        <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} />
        <span style={{ marginLeft: 6 }}>Overwrite if file exists</span>
      </label>
      <div style={{ marginTop: 8 }}>
        <button disabled={busy} onClick={handleUpload}>{busy ? 'Uploading…' : 'Upload'}</button>
      </div>
      {msg && (
        <div style={{
          marginTop: 8, padding: 8, borderRadius: 4,
          background: msg.kind === 'ok' ? '#e7f6e9' : '#fdeaea',
          color: msg.kind === 'ok' ? '#135e1a' : '#8a1f1f'
        }}>{msg.text}</div>
      )}
    </div>
  );
}

/** ------------------------------------------------------------------------
 * Scores JSON uploader (uses /api/upload/json-direct with autoCalculate=true)
 * Accepts ONLY filenames like scores_week_X.json; supports overwrite
 * ---------------------------------------------------------------------- */
function ScoresJsonImportCard({ backendBase }) {
  const [file, setFile] = useState(null);
  const [force, setForce] = useState(false);
  const [autoCalc, setAutoCalc] = useState(true);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  async function handleUpload() {
    if (!file) { setMsg({ kind: 'error', text: 'Choose a file first.' }); return; }
    const okName = /^scores[-_ ]?week[-_ ]?\d+(?:\.json)?$/i.test(file.name.trim());
    if (!okName) { setMsg({ kind: 'error', text: 'Filename must be scores_week_X.json' }); return; }

    setBusy(true); setMsg(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('force', String(force));
      fd.append('autoCalculate', String(autoCalc)); // triggers winners auto-calc on server

      const res = await fetch(`${backendBase}/api/upload/json-direct`, { method: 'POST', body: fd });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        if (res.status === 409) {
          const overwrite = window.confirm((data?.message || 'File already exists.') + '\nOverwrite?');
          if (overwrite) {
            const fd2 = new FormData();
            fd2.append('file', file);
            fd2.append('force', 'true');
            fd2.append('autoCalculate', String(autoCalc));
            const r2 = await fetch(`${backendBase}/api/upload/json-direct`, { method: 'POST', body: fd2 });
            const d2 = await r2.json().catch(() => ({}));
            if (!r2.ok) setMsg({ kind: 'error', text: d2?.error || 'Upload failed.' });
            else setMsg({ kind: 'ok', text: `Saved as ${d2.savedAs || 'scores file'}${d2.autoCalculated ? ' (winners auto-calculated)' : ''}` });
          }
        } else {
          setMsg({ kind: 'error', text: data?.error || 'Upload failed.' });
        }
      } else {
        setMsg({ kind: 'ok', text: `Saved as ${data.savedAs || 'scores file'}${data.autoCalculated ? ' (winners auto-calculated)' : ''}` });
        setFile(null); setForce(false);
      }
    } catch (e) {
      setMsg({ kind: 'error', text: e.message || 'Network error.' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ border: '1px solid #ddd', padding: 12, borderRadius: 6 }}>
      <h3 style={{ marginTop: 0 }}>Import Scores JSON (Auto-calc winners)</h3>
      <div style={{ marginBottom: 6, color: '#555' }}>
        Expected: <code>[{'{'}team1, team2, score1, score2, (optional) date{'}'}]</code>
      </div>
      <div style={{ marginBottom: 8 }}>
        <input type="file" accept=".json" onChange={(e) => setFile(e.target.files?.[0] || null)} />
      </div>
      <label style={{ display: 'inline-flex', alignItems: 'center', marginRight: 12 }}>
        <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} />
        <span style={{ marginLeft: 6 }}>Overwrite if file exists</span>
      </label>
      <label style={{ display: 'inline-flex', alignItems: 'center', marginRight: 12 }}>
        <input type="checkbox" checked={autoCalc} onChange={(e) => setAutoCalc(e.target.checked)} />
        <span style={{ marginLeft: 6 }}>Auto-calc winners</span>
      </label>
      <div style={{ marginTop: 8 }}>
        <button disabled={busy} onClick={handleUpload}>{busy ? 'Uploading…' : 'Upload'}</button>
      </div>
      {msg && (
        <div style={{
          marginTop: 8, padding: 8, borderRadius: 4,
          background: msg.kind === 'ok' ? '#e7f6e9' : '#fdeaea',
          color: msg.kind === 'ok' ? '#135e1a' : '#8a1f1f'
        }}>{msg.text}</div>
      )}
    </div>
  );
}


/** ------------------------------------------------------------------------
 * Maintenance / Test Mode control
 * ---------------------------------------------------------------------- */
function MaintenanceModeCard({ backendBase }) {
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = async () => {
    setMsg(null);
    try {
      const r = await fetch(`${backendBase}/api/maintenance-mode`, { cache: 'no-store' });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j?.error || `HTTP ${r.status}`);
      setEnabled(Boolean(j.maintenanceMode));
    } catch (e) {
      setMsg({ kind: 'error', text: 'Could not load Test / Maintenance Mode status.' });
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const setMode = async (nextEnabled) => {
    if (nextEnabled) {
      const confirmed = window.confirm(
        'Turn Test / Maintenance Mode ON? Player entry and pick submissions will be suspended until you turn it OFF.'
      );
      if (!confirmed) return;
    }

    setBusy(true);
    setMsg(null);

    const adminKey = process.env.REACT_APP_ADMIN_TOKEN || '';
    const keyParam = adminKey ? `?key=${encodeURIComponent(adminKey)}` : '';

    try {
      const r = await fetch(`${backendBase}/api/maintenance-mode${keyParam}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ maintenanceMode: nextEnabled })
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j?.error || 'Save failed.');

      const active = Boolean(j.maintenanceMode);
      setEnabled(active);
      setMsg({
        kind: 'ok',
        text: active
          ? 'TEST MODE ACTIVE — player entry and pick submissions are suspended.'
          : 'Test / Maintenance Mode is OFF — normal player access is restored.'
      });
    } catch (e) {
      setMsg({ kind: 'error', text: e.message || 'Save failed.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{
      border: enabled ? '2px solid #b91c1c' : '1px solid #ddd',
      padding: 14,
      borderRadius: 6,
      background: enabled ? '#fff4f4' : '#fff'
    }}>
      <h3 style={{ marginTop: 0, color: enabled ? '#8a1f1f' : '#222' }}>
        Test / Maintenance Mode
      </h3>

      <div style={{ marginBottom: 10 }}>
        <strong>Status: </strong>
        {enabled ? (
          <span style={{ color: '#b91c1c', fontWeight: 700 }}>ACTIVE</span>
        ) : (
          <span style={{ color: '#135e1a', fontWeight: 700 }}>OFF</span>
        )}
      </div>

      <div style={{ marginBottom: 12, color: enabled ? '#8a1f1f' : '#555' }}>
        {enabled
          ? 'Player entry and pick submissions are suspended. Admin Tools remain available.'
          : 'Normal SLFC operation. Players can enter and submit picks subject to the weekly cutoff.'}
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {enabled ? (
          <button
            onClick={() => setMode(false)}
            disabled={busy}
            style={{
              background: '#135e1a',
              color: '#fff',
              border: 'none',
              padding: '8px 14px',
              borderRadius: 4,
              cursor: busy ? 'default' : 'pointer'
            }}
          >
            {busy ? 'Saving…' : 'Turn Test Mode OFF'}
          </button>
        ) : (
          <button
            onClick={() => setMode(true)}
            disabled={busy}
            style={{
              background: '#b91c1c',
              color: '#fff',
              border: 'none',
              padding: '8px 14px',
              borderRadius: 4,
              cursor: busy ? 'default' : 'pointer'
            }}
          >
            {busy ? 'Saving…' : 'Turn Test Mode ON'}
          </button>
        )}

        <button onClick={load} disabled={busy}>Refresh Status</button>
      </div>

      {msg && (
        <div style={{
          marginTop: 10,
          padding: 8,
          borderRadius: 4,
          background: msg.kind === 'ok' ? '#e7f6e9' : '#fdeaea',
          color: msg.kind === 'ok' ? '#135e1a' : '#8a1f1f'
        }}>
          {msg.text}
        </div>
      )}
    </div>
  );
}

/** ------------------------------------------------------------------------
 * Picks Visibility control (Admin override for All Players' Picks)
 * ---------------------------------------------------------------------- */
function PicksVisibilityCard({ backendBase }) {
  const [mode, setMode] = useState('auto');       // 'on' | 'off' | 'auto'
  const [revealNow, setRevealNow] = useState(false);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setMsg(null);
    try {
      const r = await fetch(`${backendBase}/api/picks-visibility`, { cache: 'no-store' });
      if (!r.ok) throw new Error(`${r.status}`);
      const j = await r.json();
      setMode(String(j.mode || j.value || 'auto').toLowerCase());
      setRevealNow(Boolean(j.revealNow));
    } catch (e) {
      setMsg({ kind: 'error', text: 'Could not load visibility settings. Defaulting to Auto.' });
      setMode('auto'); setRevealNow(false);
    }
  };

  useEffect(() => { load(); /* on mount */ }, []); // eslint-disable-line

  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await fetch(`${backendBase}/api/picks-visibility`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode })
      });
      const j = await r.json().catch(()=> ({}));
      if (!r.ok) throw new Error(j?.error || 'Save failed.');
      setMsg({ kind: 'ok', text: `Saved: ${mode.toUpperCase()}` });
      // refresh current reveal flag after save
      load();
    } catch (e) {
      setMsg({ kind: 'error', text: e.message || 'Save failed.' });
    } finally {
      setBusy(false);
    }
  };

  const statusChip = (() => {
    if (mode === 'on') return <span style={{ color:'#135e1a', fontWeight:700 }}>Visible (ON)</span>;
    if (mode === 'off') return <span style={{ color:'#8a1f1f', fontWeight:700 }}>Hidden (OFF)</span>;
    return <span style={{ color:'#1f4b99', fontWeight:700 }}>Auto {revealNow ? '(visible now)' : '(hidden until schedule)'}</span>;
  })();

  return (
    <div style={{ border: '1px solid #ddd', padding: 12, borderRadius: 6 }}>
      <h3 style={{ marginTop: 0 }}>Picks Visibility (Admin Override)</h3>
      <div style={{ marginBottom: 8, color: '#555' }}>
        Control whether All Players' Picks are visible. <strong>{statusChip}</strong>
      </div>

      <div style={{ display:'flex', gap:16, alignItems:'center', marginBottom: 8 }}>
        <label style={{ display:'inline-flex', alignItems:'center' }}>
          <input type="radio" name="pv" value="off" checked={mode==='off'} onChange={()=>setMode('off')} />
          <span style={{ marginLeft: 6 }}>Off (always hidden)</span>
        </label>
        <label style={{ display:'inline-flex', alignItems:'center' }}>
          <input type="radio" name="pv" value="auto" checked={mode==='auto'} onChange={()=>setMode('auto')} />
          <span style={{ marginLeft: 6 }}>Auto (Thu 1:00 PM CT)</span>
        </label>
        <label style={{ display:'inline-flex', alignItems:'center' }}>
          <input type="radio" name="pv" value="on" checked={mode==='on'} onChange={()=>setMode('on')} />
          <span style={{ marginLeft: 6 }}>On (visible now)</span>
        </label>
      </div>

      <div style={{ display:'flex', gap:10 }}>
        <button onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
        <button onClick={load} disabled={busy}>Refresh</button>
      </div>

      {msg && (
        <div style={{
          marginTop: 8, padding: 8, borderRadius: 4,
          background: msg.kind === 'ok' ? '#e7f6e9' : '#fdeaea',
          color: msg.kind === 'ok' ? '#135e1a' : '#8a1f1f'
        }}>{msg.text}</div>
      )}
    </div>
  );
}

/** ------------------------------------------------------------------------
 * Picks Stats (Submitted / Missing) — current week with refresh
 * ---------------------------------------------------------------------- */
function PicksStatsCard({ backendBase }) {
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showMissing, setShowMissing] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await fetch(`${backendBase}/api/stats/picks`, { cache: 'no-store' });
      const j = await r.json();
      if (!r.ok) throw new Error(j?.error || `HTTP ${r.status}`);
      setStats(j);
    } catch (e) {
      setMsg({ kind: 'error', text: 'Could not load picks stats.' });
    } finally { setBusy(false); }
  };

  useEffect(() => { load(); }, []); // on mount

  return (
    <div style={{ border: '1px solid #ddd', padding: 12, borderRadius: 6 }}>
      <h3 style={{ marginTop: 0 }}>Submitted Picks (Current Week)</h3>
      {stats ? (
        <div style={{ color: '#333' }}>
          <div style={{ marginBottom: 8 }}>
            <strong>Week {stats.week}</strong> — Submitted <strong>{stats.submitted_count}</strong> / Total <strong>{stats.total_roster}</strong> — Missing <strong>{stats.missing_count}</strong>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 8 }}>
            <button onClick={load} disabled={busy}>{busy ? 'Refreshing…' : 'Refresh'}</button>
            <label style={{ display: 'inline-flex', alignItems: 'center' }}>
              <input type="checkbox" checked={showMissing} onChange={(e)=>setShowMissing(e.target.checked)} />
              <span style={{ marginLeft: 6 }}>Show names missing</span>
            </label>
            <span style={{ marginLeft: 'auto', color: '#666', fontSize: 12 }}>as of {stats.now_cst}</span>
          </div>
          {showMissing && (
            <div style={{ background: '#f9fafb', border: '1px dashed #ddd', padding: 8, borderRadius: 6 }}>
              {stats.names_missing && stats.names_missing.length ? (
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {stats.names_missing.map(n => <li key={n}>{n}</li>)}
                </ul>
              ) : (
                <em>Everyone has submitted.</em>
              )}
            </div>
          )}
        </div>
      ) : (
        <div style={{ marginBottom: 8, color: '#666' }}>Loading…</div>
      )}

      {msg && (
        <div style={{
          marginTop: 8, padding: 8, borderRadius: 4,
          background: msg.kind === 'ok' ? '#e7f6e9' : '#fdeaea',
          color: msg.kind === 'ok' ? '#135e1a' : '#8a1f1f'
        }}>{msg.text}</div>
      )}
    </div>
  );
}

/** ------------------------------------------------------------------------
 * Sidecar Panel — SIMPLE (Coach version)
 * Week + From/To (dates only). CST-only window:
 *   Start day fixed to 2:00 PM CST
 *   End day   fixed to 11:59 PM CST
 * Two buttons:
 *   - Download Spreads (NCAAF+NFL combined) → games_week_<week>.json
 *   - Download Scores  (NCAAF+NFL combined) → scores_week_<week>.json
 * Does NOT save anything on the server. Pure client download.
 * ---------------------------------------------------------------------- */
function SidecarPanel({ backendBase }) {
  const [week, setWeek] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  async function simpleDownload(kind) {
    if (!week) { setMsg({ kind: 'error', text: 'Enter week number.' }); return; }
    if (!from || !to) { setMsg({ kind: 'error', text: 'Pick From and To dates.' }); return; }
    setBusy(true); setMsg(null);
    try {
      const url = `${backendBase}/api/sidecar/download-${kind}?week=${encodeURIComponent(week)}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
      const resp = await fetch(url);
      if (!resp.ok) {
        const t = await resp.text().catch(()=> ''); 
        throw new Error(`${resp.status} ${t || resp.statusText}`);
      }
      const blob = await resp.blob();
      const disp = resp.headers.get('Content-Disposition') || '';
      const m = /filename="(.+?)"/.exec(disp);
      const filename = m ? m[1] : `${kind}_week_${week}.json`;

      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(()=>URL.revokeObjectURL(a.href), 2000);

      setMsg({ kind: 'ok', text: `Downloaded ${filename}` });
    } catch (e) {
      console.error(e);
      setMsg({ kind: 'error', text: `Download failed: ${e.message}` });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ border: '1px solid #ddd', padding: 12, borderRadius: 6 }}>
      <h3 style={{ marginTop: 0 }}>Sidecar (JsonOdds) — Download JSON (CST)</h3>
      <div style={{ display:'flex', gap:12, alignItems:'center', flexWrap:'wrap' }}>
        <label>Week:&nbsp;
          <input type="number" min="1" value={week} onChange={e=>setWeek(e.target.value)} style={{ width:80 }} />
        </label>
        <label>From:&nbsp;
          <input type="date" value={from} onChange={e=>setFrom(e.target.value)} />
        </label>
        <label>To:&nbsp;
          <input type="date" value={to} onChange={e=>setTo(e.target.value)} />
        </label>
        <button onClick={()=>simpleDownload('spreads')} disabled={busy}>Download Spreads (NCAAF+NFL)</button>
        <button onClick={()=>simpleDownload('scores')}  disabled={busy}>Download Scores (NCAAF+NFL)</button>
      </div>
      <div style={{ marginTop: 8, color:'#555', fontSize:12 }}>
        Window uses <b>CST only</b> with fixed boundaries: start day at <b>2:00 PM</b>, end day at <b>11:59 PM</b>.
        Nothing is imported or saved on the server by these buttons.
      </div>
      {msg && (
        <div style={{ marginTop:10, fontWeight:600, color: msg.kind==='ok' ? '#135e1a' : '#8a1f1f' }}>
          {msg.text}
        </div>
      )}
    </div>
  );
}

/** ------------------------------------------------------------------------
 * Main Admin Tools Page
 * ---------------------------------------------------------------------- */
export default function AdminToolsPage() {
  const [rosterFile, setRosterFile] = useState(null);
  const [spreadFile, setSpreadFile] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [debugWeek, setDebugWeek] = useState(null);
  const [debugFiles, setDebugFiles] = useState({});

  // Deployment-friendly base URL
  const backendBase =
    process.env.REACT_APP_API_BASE_URL ||
    process.env.REACT_APP_API_URL ||
    (window.location.hostname === 'localhost'
      ? 'http://localhost:5001'
      : 'https://pickem-backend-2025.onrender.com');

  useEffect(() => {
    if (sessionStorage.getItem('isAdmin') === 'true') setIsAdmin(true);
  }, []);

  const handleLogin = () => {
    if (username === 'Admin' && password === 'letmein') {
      sessionStorage.setItem('isAdmin', 'true');
      setIsAdmin(true);
      setError('');
    } else {
      setError('Incorrect admin credentials');
    }
  };

  const uploadFile = async (file, endpoint, force = false) => {
    if (!file) return alert('Please select a file first.');
    const formData = new FormData();
    formData.append('file', file);
    formData.append('force', force.toString());
    try {
      const res = await fetch(`${backendBase}${endpoint}`, { method: 'POST', body: formData });
      if (res.status === 409) {
        const data = await res.json();
        const confirm = window.confirm(`${data?.message || 'File already exists.'}\nOverwrite?`);
        if (confirm) return uploadFile(file, endpoint, true);
        return;
      }
      const text = await res.text();
      if (!res.ok) alert(`❌ Upload failed: ${text}`); else alert(text);
    } catch (err) {
      console.error(err); alert('❌ Upload failed. Please try again.');
    }
  };

  const handleSystemReset = async () => {
    if (!window.confirm('⚠️ This will delete all games, scores, picks, and reset to Week 1. Continue?')) return;
    try {
      const res = await fetch(`${backendBase}/api/reset-system`, { method: 'POST' });
      const text = await res.text(); alert(text);
    } catch (err) { console.error('Reset failed:', err); alert('Reset failed. Check server logs.'); }
  };

  // --- Clear Chat (calls backend /api/admin/clear-chat) ---
  const handleClearChat = async () => {
    if (!window.confirm('Delete ALL chat messages? This cannot be undone.')) return;

    const adminKey = process.env.REACT_APP_ADMIN_TOKEN || '';
    const keyParam = adminKey ? `?key=${encodeURIComponent(adminKey)}` : '';

    try {
      const res = await fetch(`${backendBase}/api/admin/clear-chat${keyParam}`, {
        method: 'POST',
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) {
        alert('Chat cleared. (Backup saved in /data/backups)');
      } else {
        alert(`Failed to clear chat: ${data.error || res.statusText}`);
      }
    } catch (e) {
      alert(`Failed to clear chat: ${e.message}`);
    }
  };

  const fetchDebugFiles = async () => {
    try {
      const wkRes = await fetch(`${backendBase}/data/current_week.json`, { cache: 'no-store' });
      const wk = await wkRes.json();
      const current = wk.currentWeek ?? wk.week ?? 1;
      setDebugWeek(current);

      const keys = ['games', 'scores', 'picks', 'winners'];
      const statuses = {};
      await Promise.all(keys.map(async (k) => {
        const r = await fetch(`${backendBase}/data/${k}_week_${current}.json`, { cache: 'no-store' });
        statuses[k] = r.ok;
      }));
      setDebugFiles(statuses);
    } catch {
      setDebugFiles({});
    }
  };

  useEffect(() => { if (isAdmin) fetchDebugFiles(); }, [isAdmin]);

  return (
    <div style={{ padding: 20 }}>
      {!isAdmin ? (
        <>
          <h2>Admin Login</h2>
          <input type="text" placeholder="Username" value={username}
                 onChange={(e)=>setUsername(e.target.value)} style={{ marginRight: 10 }} />
          <input type="password" placeholder="Password" value={password}
                 onChange={(e)=>setPassword(e.target.value)} style={{ marginRight: 10 }} />
          <button onClick={handleLogin}>Login</button>
          {error && <p style={{ color: 'red' }}>{error}</p>}
        </>
      ) : (
        <>
          <h1 style={{ fontSize: 26, marginBottom: 20 }}>Welcome to the Football Pick'em Game</h1>
          <h2>Administration Page</h2>

          {/* Test / Maintenance Mode */}
          <section style={{ marginTop: 24, marginBottom: 24 }}>
            <MaintenanceModeCard backendBase={backendBase} />
          </section>

          {/* NEW: Picks stats (current week) */}
          <section style={{ marginTop: 24, marginBottom: 24 }}>
            <PicksStatsCard backendBase={backendBase} />
          </section>

          {/* Picks Visibility override */}
          <section style={{ marginTop: 24, marginBottom: 24 }}>
            <PicksVisibilityCard backendBase={backendBase} />
          </section>

          {/* Games JSON (no auto-calc) */}
          <section style={{ marginTop: 24, marginBottom: 24 }}>
            <JsonUpload backendBase={backendBase} />
          </section>

          {/* Scores JSON (auto-calc winners) */}
          <section style={{ marginTop: 24, marginBottom: 24 }}>
            <ScoresJsonImportCard backendBase={backendBase} />
          </section>

          {/* Sidecar — simplified download panel */}
          <section style={{ marginTop: 24, marginBottom: 24 }}>
            <SidecarPanel backendBase={backendBase} />
          </section>

          {/* Legacy Excel tools (kept, still useful) */}
          <div style={{ marginTop: 20 }}>
            <h3>Upload Player Roster (Excel)</h3>
            <input type="file" onChange={(e)=>setRosterFile(e.target.files[0])} />
            <button onClick={()=>uploadFile(rosterFile, '/api/upload/roster')}>Import File to JSON</button>
          </div>

          <div style={{ marginTop: 20 }}>
            <h3>Upload Sportsbook Spread (Excel, raw format)</h3>
            <input type="file" onChange={(e)=>setSpreadFile(e.target.files[0])} />
            <button onClick={()=>uploadFile(spreadFile, '/api/upload/spread')}>Import File to JSON</button>
          </div>

          <div style={{ marginTop: 40 }}>
            <h3 style={{ color: 'darkred' }}>Danger Zone</h3>
            <button
              style={{ background: '#c0392b', color: '#fff', padding: '8px 16px', border: 'none', borderRadius: 4, marginRight: 10 }}
              onClick={handleSystemReset}
            >
              Reset Entire System
            </button>

            {/* Clear Chat Messages */}
            <button
              onClick={handleClearChat}
              style={{
                background: '#b91c1c',
                color: '#fff',
                border: 'none',
                padding: '8px 12px',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Clear Chat Messages
            </button>
            <div style={{ fontSize: 12, color: '#555', marginTop: 6 }}>
              Deletes all chat messages (previous file backed up in <code>/data/backups</code>).
            </div>
          </div>

          <div style={{ marginTop: 40, borderTop: '1px solid #ccc', paddingTop: 20 }}>
            <h3>🛠 Debug Panel</h3>
            <button onClick={fetchDebugFiles} style={{ marginBottom: 10 }}>🔄 Refresh Debug Info</button>
            <p><strong>Current Week:</strong> {debugWeek ?? 'Loading...'}</p>
            {!!debugFiles && (
              <ul>
                {['games','scores','picks','winners'].map(k => (
                  <li key={k}>{k}_week_{debugWeek} — {debugFiles[k] ? '✅ Found' : '❌ Missing'}</li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}

